/**
 * PillInput - bottom-anchored pill prompt textarea for /chat.
 *
 * MD-31 ASKLAW-EMBER-THEME Phase 2. Per spec F-FE-THEME-GEMINI §7.1 + AC-08.
 *
 * Shape:
 *   - pill --radius-full, height auto, min-height 56px, padding 16px 56px 16px 24px
 *   - bg var(--asklaw-paper-elev), border 1px var(--asklaw-border-strong), shadow-md
 *   - focus: border var(--asklaw-accent), shadow var(--shadow-focus)
 *
 * Auto-resize textarea: grow 1 -> 5 line via scrollHeight measurement (manual,
 * KHONG dep `react-textarea-autosize` - keep bundle small).
 *
 * Right cluster: Mic icon 36x36 (placeholder Phase 2 no functionality) +
 *   Send button 40x40 filled gradient pill.
 *
 * Disabled state when atHardCap or streaming.
 *
 * Mobile 360px: prompt input >= 56px touch target per AC-13.
 */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { MicIcon, SendIcon, SquareIcon } from './icons';

export type PillInputHandle = {
  focus: () => void;
  setValue: (text: string) => void;
  getValue: () => string;
  clear: () => void;
  element: HTMLTextAreaElement | null;
};

export type PillInputProps = {
  placeholder: string;
  hint?: string;
  disabled?: boolean;
  isStreaming?: boolean;
  ariaLabel: string;
  onSubmit: (value: string) => void;
  onStop?: () => void;
  /** Maximum length (default 2000) */
  maxLength?: number;
  /** Min rows (default 1) */
  minRows?: number;
  /** Max rows (default 5) */
  maxRows?: number;
};

const LINE_HEIGHT_PX = 24; // matches CSS line-height for textarea
const VERTICAL_PADDING_PX = 32; // 16 top + 16 bottom

function clampRows(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export const PillInput = forwardRef<PillInputHandle, PillInputProps>(
  function PillInput(
    {
      placeholder,
      hint,
      disabled = false,
      isStreaming = false,
      ariaLabel,
      onSubmit,
      onStop,
      maxLength = 2000,
      minRows = 1,
      maxRows = 5,
    },
    ref
  ) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const autosize = useCallback(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.style.height = 'auto';
      const minPx = LINE_HEIGHT_PX * minRows + VERTICAL_PADDING_PX;
      const maxPx = LINE_HEIGHT_PX * maxRows + VERTICAL_PADDING_PX;
      const targetPx = clampRows(ta.scrollHeight, minPx, maxPx);
      ta.style.height = `${targetPx}px`;
      ta.style.overflowY = ta.scrollHeight > maxPx ? 'auto' : 'hidden';
    }, [minRows, maxRows]);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => textareaRef.current?.focus(),
        setValue: (text: string) => {
          if (textareaRef.current) {
            textareaRef.current.value = text;
            autosize();
          }
        },
        getValue: () => textareaRef.current?.value ?? '',
        clear: () => {
          if (textareaRef.current) {
            textareaRef.current.value = '';
            autosize();
          }
        },
        element: textareaRef.current,
      }),
      [autosize]
    );

    useEffect(() => {
      autosize();
    }, [autosize]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          // VN IME (Telex/Unikey/macOS Vietnamese) fires Enter keydown to commit
          // a pending composition; treating it as a send double-submits the
          // question (BUG-2026-06-12 Bug 3 defense-in-depth). 229 = legacy
          // keyCode browsers report for in-composition keydown.
          if (e.nativeEvent.isComposing || e.keyCode === 229) return;
          e.preventDefault();
          if (disabled) return;
          const value = (e.currentTarget.value || '').trim();
          if (!value) return;
          onSubmit(value);
        }
      },
      [disabled, onSubmit]
    );

    const handleInput = useCallback(() => {
      autosize();
    }, [autosize]);

    const handleSendClick = useCallback(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      const value = (ta.value || '').trim();
      if (!value || disabled) return;
      onSubmit(value);
    }, [disabled, onSubmit]);

    return (
      <div className="pill-input-wrap" data-pill-input>
        <div className={`pill-input ${disabled ? 'pill-input--disabled' : ''}`}>
          <textarea
            ref={textareaRef}
            className="pill-input__textarea"
            placeholder={placeholder}
            aria-label={ariaLabel}
            maxLength={maxLength}
            rows={minRows}
            disabled={disabled}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
          />
          <div className="pill-input__actions">
            <button
              type="button"
              className="pill-input__mic"
              aria-label={
                ariaLabel.includes('Vietnamese') || ariaLabel.includes('câu hỏi')
                  ? 'Nhập bằng giọng nói (sắp ra mắt)'
                  : 'Voice input (coming soon)'
              }
              disabled
              title="Coming soon"
            >
              <MicIcon size={18} strokeWidth={1.6} />
            </button>
            {isStreaming && onStop ? (
              <button
                type="button"
                className="pill-input__send pill-input__send--stop"
                onClick={onStop}
                aria-label="Dừng tạo / Stop"
              >
                <SquareIcon size={18} />
              </button>
            ) : (
              <button
                type="button"
                className="pill-input__send"
                onClick={handleSendClick}
                disabled={disabled}
                aria-label={
                  ariaLabel.includes('câu hỏi')
                    ? 'Gửi câu hỏi'
                    : 'Send question'
                }
              >
                <SendIcon size={18} />
              </button>
            )}
          </div>
        </div>
        {hint ? (
          <p className="pill-input__hint" aria-hidden="false">
            {hint}
          </p>
        ) : null}
      </div>
    );
  }
);

export default PillInput;
